import { createContext, useContext, useState, useCallback } from "react";
import type { ReactNode } from "react";
import type { Dispatch, SetStateAction } from "react";
import { toPositiveIntId } from "../utils/dealAssetUpdateSync";
import type {
  Deal,
  PartialDeal,
  Asset,
  Tenant,
  LoanComp,
  Contact,
} from "../types/deal";

type DealField = keyof Deal;
type DealForm = PartialDeal;
type AssetForm = Partial<Asset>;
type TenantForm = Partial<Tenant>;
type LoanCompForm = Partial<LoanComp>;
type ContactForm = Partial<Contact>;

interface DealContextType {
  deal: DealForm;
  updateField: <K extends DealField>(field: K, value: DealForm[K]) => void;
  setDeal: (deal: DealForm) => void;
  resetDeal: () => void;
  assets: AssetForm[];
  setAssets: Dispatch<SetStateAction<AssetForm[]>>;
  tenants: TenantForm[];
  setTenants: Dispatch<SetStateAction<TenantForm[]>>;
  contacts: ContactForm[];
  setContacts: Dispatch<SetStateAction<ContactForm[]>>;

  // Unified update functions for tenants
  updateTenant: (
    tenantId: number | string,
    updates: Partial<TenantForm>,
  ) => void;
  addTenant: (tenant: TenantForm) => void;
  removeTenant: (tenantId: number | string) => void;
  loanComps: LoanCompForm[];
  setLoanComps: Dispatch<SetStateAction<LoanCompForm[]>>;

  /** True when any tenant asset_name fails deal validation (see ExtractedTenantTable). */
  tenantAssetNameFieldHasError: boolean;
  setTenantAssetNameFieldHasError: Dispatch<SetStateAction<boolean>>;

  /** Persisted asset ids that need a PUT when the deal is saved (tenant rows edited in extraction tables, etc.). */
  assetIdsNeedingSave: number[];
  clearAssetsNeedingSave: () => void;
}

const DealContext = createContext<DealContextType | undefined>(undefined);

export const DealProvider = ({ children }: { children: ReactNode }) => {
  const [deal, setDealState] = useState<DealForm>({});
  const [assets, setAssetsState] = useState<AssetForm[]>([]);
  const [tenants, setTenantsState] = useState<TenantForm[]>([]);
  const [contacts, setContactsState] = useState<ContactForm[]>([]);
  const [loanComps, setLoanCompsState] = useState<LoanCompForm[]>([]);
  const [tenantAssetNameFieldHasError, setTenantAssetNameFieldHasError] =
    useState(false);
  const [assetIdsNeedingSave, setAssetIdsNeedingSave] = useState<number[]>([]);

  const markAssetForDealSave = useCallback((raw: unknown) => {
    const id = toPositiveIntId(raw);
    if (id == null) return;
    setAssetIdsNeedingSave((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const clearAssetsNeedingSave = useCallback(() => {
    setAssetIdsNeedingSave([]);
  }, []);
  const updateField = <K extends DealField>(field: K, value: DealForm[K]) => {
    const cleanedValue = value === "" || value === undefined ? null : value;
    setDealState((prev) => ({ ...prev, [field]: cleanedValue }));
  };

  const setDeal = (dealObj: DealForm) => {
    setDealState(dealObj);
  };

  const resetDeal = () => {
    setDealState({});
    setAssetsState([]);
    setTenantsState([]);
    setContactsState([]);
    setLoanCompsState([]);
    setTenantAssetNameFieldHasError(false);
    setAssetIdsNeedingSave([]);
  };

  // Unified tenant update function
  const updateTenant = (
    tenantId: number | string,
    updates: Partial<TenantForm>,
  ) => {
    setTenantsState((prev) => {
      const prevRow = prev.find((t) => String(t.id) === String(tenantId));
      const parentAssetId =
        toPositiveIntId(
          (updates as any).asset_id ?? (updates as any).asset?.id,
        ) ??
        toPositiveIntId(
          (prevRow as any)?.asset_id ?? (prevRow as any)?.asset?.id,
        );
      if (parentAssetId != null) {
        markAssetForDealSave(parentAssetId);
      }

      return prev.map((tenant) =>
        String(tenant.id) === String(tenantId)
          ? { ...tenant, ...updates }
          : tenant,
      );
    });

    // Mirror the tenant row onto nested `asset.tenants` only for that asset row.
    // Never map over all assets rewriting `tenants` — undefined||[] marks every asset dirty vs original.
    const targetAssetId =
      updates.asset_id ?? (updates.asset as { id?: unknown } | undefined)?.id;
    if (targetAssetId != null && `${targetAssetId}` !== "") {
      setAssetsState((prev) =>
        prev.map((asset) => {
          if (String(asset.id) !== String(targetAssetId)) return asset;

          const current = asset.tenants ?? [];
          const nextTenants = current.map((tenant) =>
            String(tenant.id) === String(tenantId)
              ? { ...tenant, ...updates }
              : tenant,
          );

          return { ...asset, tenants: nextTenants };
        }),
      );
    }
  };

  // Add tenant function
  const addTenant = (tenant: TenantForm) => {
    setTenantsState((prev) => [...prev, tenant]);

    markAssetForDealSave((tenant as any).asset_id ?? (tenant as any).asset?.id);

    // Add to nested assets if asset_id exists. Compare as strings (like
    // updateTenant) so a numeric-string asset_id still matches a numeric asset id.
    const targetAssetId = tenant.asset_id as unknown;
    if (targetAssetId != null && `${targetAssetId}` !== "") {
      setAssetsState((prev) =>
        prev.map((asset) =>
          asset.id != null && String(asset.id) === String(targetAssetId)
            ? { ...asset, tenants: [...(asset.tenants || []), tenant as any] }
            : asset,
        ),
      );
    }
  };

  // Remove tenant function
  const removeTenant = (tenantId: number | string) => {
    setTenantsState((prev) => {
      const removed = prev.find((t) => String(t.id) === String(tenantId));
      const aid = toPositiveIntId(
        (removed as any)?.asset_id ?? (removed as any)?.asset?.id,
      );
      if (aid != null) markAssetForDealSave(aid);
      return prev.filter((tenant) => String(tenant.id) !== String(tenantId));
    });

    // Remove from nested assets
    setAssetsState((prev) =>
      prev.map((asset) => {
        const current = asset.tenants || [];
        const nextTenants = current.filter(
          (tenant) => String(tenant.id) !== String(tenantId),
        );
        // Avoid rewriting the asset object unless something actually changed.
        if (nextTenants.length === current.length) return asset;
        return { ...asset, tenants: nextTenants as any };
      }),
    );
  };

  return (
    <DealContext.Provider
      value={{
        deal,
        updateField,
        setDeal,
        resetDeal,
        assets,
        setAssets: setAssetsState,
        tenants,
        setTenants: setTenantsState,
        contacts,
        setContacts: setContactsState,
        updateTenant,
        addTenant,
        removeTenant,
        loanComps,
        setLoanComps: setLoanCompsState,
        tenantAssetNameFieldHasError,
        setTenantAssetNameFieldHasError,
        assetIdsNeedingSave,
        clearAssetsNeedingSave,
      }}
    >
      {children}
    </DealContext.Provider>
  );
};

export const useDealProvider = () => {
  const context = useContext(DealContext);
  if (!context) {
    throw new Error("useDealProvider must be used within a DealProvider");
  }
  return context;
};
